import { BaseService } from './base.service';
import { 
  MetricDetails, 
  MetricType, 
  RelatedMetric, 
  DrillDownAnalytics, 
  UserDrillPreferences,
  MetricRelationship 
} from '@/types/drill-down';

export class DrillDownService extends BaseService {
  private static readonly API_BASE = '/api/drill-down';

  async getMetricDetails(
    userId: string, 
    metricType: MetricType, 
    timeRange: string = '30d'
  ): Promise<{ success: boolean; data?: MetricDetails; error?: string }> {
    try {
      const response = await this.get(`${DrillDownService.API_BASE}/${userId}/${metricType}/details`, {
        params: { timeRange }
      });
      
      return this.handleResponse(response);
    } catch (error) {
      console.error(`Error fetching metric details for ${metricType}:`, error);
      return { success: false, error: this.getErrorMessage(error) };
    }
  }

  async getRelatedMetrics(
    userId: string, 
    metricType: MetricType
  ): Promise<{ success: boolean; data?: RelatedMetric[]; error?: string }> {
    try {
      const response = await this.get(`${DrillDownService.API_BASE}/${userId}/${metricType}/related`);
      
      return this.handleResponse(response);
    } catch (error) {
      console.error(`Error fetching related metrics for ${metricType}:`, error);
      return { success: false, error: this.getErrorMessage(error) };
    }
  }

  async getAnalysisOptions(
    userId: string, 
    metricType: MetricType
  ): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await this.get(`${DrillDownService.API_BASE}/${userId}/${metricType}/analysis`);
      
      return this.handleResponse(response);
    } catch (error) {
      console.error(`Error fetching analysis options for ${metricType}:`, error);
      return { success: false, error: this.getErrorMessage(error) };
    }
  }

  async trackDrillDownPath(
    userId: string, 
    analytics: Omit<DrillDownAnalytics, 'userId' | 'createdAt'>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await this.post(`${DrillDownService.API_BASE}/${userId}/track-path`, {
        ...analytics,
        userId,
        createdAt: new Date()
      });
      
      return this.handleResponse(response);
    } catch (error) {
      console.error('Error tracking drill-down path:', error);
      return { success: false, error: this.getErrorMessage(error) };
    }
  }

  async getUserPreferences(
    userId: string
  ): Promise<{ success: boolean; data?: UserDrillPreferences; error?: string }> {
    try {
      const response = await this.get(`${DrillDownService.API_BASE}/${userId}/preferences`);
      
      return this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      return { success: false, error: this.getErrorMessage(error) };
    }
  }

  async updateUserPreferences(
    userId: string, 
    preferences: Partial<UserDrillPreferences>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await this.put(`${DrillDownService.API_BASE}/${userId}/preferences`, {
        ...preferences,
        updatedAt: new Date()
      });
      
      return this.handleResponse(response);
    } catch (error) {
      console.error('Error updating user preferences:', error);
      return { success: false, error: this.getErrorMessage(error) };
    }
  }

  async getMetricRelationships(
    primaryMetric: MetricType
  ): Promise<{ success: boolean; data?: MetricRelationship[]; error?: string }> {
    try {
      const response = await this.get(`${DrillDownService.API_BASE}/relationships/${primaryMetric}`);
      
      return this.handleResponse(response);
    } catch (error) {
      console.error(`Error fetching metric relationships for ${primaryMetric}:`, error);
      return { success: false, error: this.getErrorMessage(error) };
    }
  }

  async getFilteredRounds(
    userId: string, 
    metricType: MetricType, 
    filters: Record<string, any>
  ): Promise<{ success: boolean; data?: any[]; error?: string }> {
    try {
      const response = await this.get(`${DrillDownService.API_BASE}/${userId}/${metricType}/rounds`, {
        params: filters
      });
      
      return this.handleResponse(response);
    } catch (error) {
      console.error(`Error fetching filtered rounds for ${metricType}:`, error);
      return { success: false, error: this.getErrorMessage(error) };
    }
  }

  async getCorrelationAnalysis(
    userId: string, 
    primaryMetric: MetricType, 
    compareMetrics: MetricType[]
  ): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await this.post(`${DrillDownService.API_BASE}/${userId}/correlation`, {
        primaryMetric,
        compareMetrics
      });
      
      return this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching correlation analysis:', error);
      return { success: false, error: this.getErrorMessage(error) };
    }
  }

  async getMetricInsights(
    userId: string, 
    metricType: MetricType, 
    timeRange: string = '30d'
  ): Promise<{ success: boolean; data?: { insights: string[]; recommendations: string[] }; error?: string }> {
    try {
      const response = await this.get(`${DrillDownService.API_BASE}/${userId}/${metricType}/insights`, {
        params: { timeRange }
      });
      
      return this.handleResponse(response);
    } catch (error) {
      console.error(`Error fetching metric insights for ${metricType}:`, error);
      return { success: false, error: this.getErrorMessage(error) };
    }
  }

  async getMetricTimeline(
    userId: string, 
    metricType: MetricType, 
    timeRange: string = '30d'
  ): Promise<{ success: boolean; data?: any[]; error?: string }> {
    try {
      const response = await this.get(`${DrillDownService.API_BASE}/${userId}/${metricType}/timeline`, {
        params: { timeRange }
      });
      
      return this.handleResponse(response);
    } catch (error) {
      console.error(`Error fetching metric timeline for ${metricType}:`, error);
      return { success: false, error: this.getErrorMessage(error) };
    }
  }

  getMetricDisplayConfig(metricType: MetricType): {
    title: string;
    description: string;
    icon: string;
    color: string;
    format: 'number' | 'percentage' | 'decimal';
  } {
    const configs = {
      activity: {
        title: 'Activity',
        description: 'Golf rounds and playing frequency',
        icon: 'activity',
        color: 'blue',
        format: 'number' as const
      },
      scoring: {
        title: 'Scoring Average',
        description: 'Average score performance',
        icon: 'target',
        color: 'green',
        format: 'decimal' as const
      },
      handicap: {
        title: 'Handicap Index',
        description: 'Official handicap progression',
        icon: 'trophy',
        color: 'purple',
        format: 'decimal' as const
      },
      putting: {
        title: 'Putting',
        description: 'Putts per round average',
        icon: 'circle',
        color: 'orange',
        format: 'decimal' as const
      },
      gir: {
        title: 'Greens in Regulation',
        description: 'Percentage of greens hit in regulation',
        icon: 'target',
        color: 'green',
        format: 'percentage' as const
      },
      fairway: {
        title: 'Fairway Accuracy',
        description: 'Percentage of fairways hit from tee',
        icon: 'activity',
        color: 'blue',
        format: 'percentage' as const
      },
      scrambling: {
        title: 'Scrambling',
        description: 'Up and down success rate',
        icon: 'star',
        color: 'yellow',
        format: 'percentage' as const
      }
    };

    return configs[metricType] || configs.activity;
  }

  generateSessionId(): string {
    return `drilldown_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}